import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GraphData, ORGANISATION_MODEL_KEY } from '../common/linqset-model';
import { Organisation } from '../common/organisation';
import { LENSES, LensKey, modelColours } from '../lenses/lens';
import { MODEL_API, RepositoryService } from '../linq-utils/repository.service';
import { InsightsService } from '../model-services/insights.service';
import { InsightsRefreshService } from '../model-services/insights-refresh.service';
import { NodeCorrectionService } from './node-correction.service';
import { buildContextGraph, ContextGraph, ContextGraphData, ContextSubject, subjectForNode, subjectForRecommendation } from './context-graph';
import { ContextGraphDialogComponent, ContextGraphDialogData } from './context-graph-dialog.component';
import { AnalysisScope, filterForScope, ScopeService } from './scope';

const COMPONENT = 'Context graph';
// a second look within this long reuses what was loaded, so opening several contexts in a row is quick
const CACHE_MS = 2 * 60 * 1000;

// what the context is about: a subject the page already knows, a recommendation, or a node's key or name
export type ContextRequest =
    | { subject: ContextSubject }
    | { recommendation: { focus?: string; subjects: string[]; title?: string } }
    | { node: string };

// what the page already has, so nothing needs loading
export interface ContextSource {
    graph: ContextGraphData;
    organisationModel?: GraphData;      // its roles and systems, for their names
}

// Opens the context of something an analysis view points at as a small graph, from what the page has or by loading the
// analysis of the models it looks at, with a way into the view that shows it in full
@Injectable({ providedIn: 'root' })
export class ContextGraphService {
    private cache?: { key: string; at: number; data: Promise<{ graph: ContextGraphData; names: Map<string, string> }> };

    constructor(private dialog: MatDialog, private repoService: RepositoryService, private insightsService: InsightsService,
        private scopeService: ScopeService, private router: Router, private refreshes: InsightsRefreshService,
        private corrections: NodeCorrectionService) {}

    open(organisation: Organisation, scope: AnalysisScope, request: ContextRequest, name?: string, source?: ContextSource) {
        const data: ContextGraphDialogData = {
            name: name || ('recommendation' in request ? request.recommendation.title : 'subject' in request ? request.subject.name : undefined),
            graph: this.build(organisation, scope, request, source),
            openLens: (lens, focus) => this.openLens(organisation, scope, lens, focus),
            openNode: (key, modelKey) => this.corrections.openInModel(organisation, modelKey, key, scope.workspaceKey),
        };
        this.dialog.open(ContextGraphDialogComponent, { data, width: 'min(1100px, 94vw)', maxWidth: '94vw', autoFocus: 'dialog', ariaLabel: `Context of ${data.name || 'this'}` });
    }

    private async build(organisation: Organisation, scope: AnalysisScope, request: ContextRequest, source?: ContextSource): Promise<ContextGraph | undefined> {
        const { graph, names } = source ? { graph: source.graph, names: this.namesOf(source.organisationModel) } : await this.load(organisation, scope);
        // names of what the graph holds, for finding a subject by name
        const allNames = new Map(names);
        const subject = 'subject' in request ? request.subject
            : 'recommendation' in request ? subjectForRecommendation(request.recommendation, graph, allNames)
            : subjectForNode(graph, allNames, request.node);
        return subject && buildContextGraph(graph, subject, allNames, modelColours(organisation));
    }

    // the analysis graph of the models looked at, and the names of the organisation's roles and systems
    private load(organisation: Organisation, scope: AnalysisScope) {
        // what was loaded before the insights were last worked out again is stale
        const key = `${organisation._key}|${JSON.stringify(scope)}|${this.refreshes.version}`;
        if (this.cache && this.cache.key === key && Date.now() - this.cache.at < CACHE_MS) return this.cache.data;
        const data = Promise.all([
            this.insightsService.loadAnalysisGraphForFilter(organisation, filterForScope(scope)) as Promise<ContextGraphData>,
            this.insightsService.afterRefresh(scope.workspaceKey, () => this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(organisation._key, ORGANISATION_MODEL_KEY, scope.workspaceKey)), COMPONENT, 'Load the organisation model')),
        ]).then(([graph, organisationModel]) => ({ graph: graph || { nodes: [], edges: [] }, names: this.namesOf(organisationModel) }));
        this.cache = { key, at: Date.now(), data };
        data.catch(() => { if (this.cache?.data === data) this.cache = undefined; });
        return data;
    }

    private namesOf(organisationModel?: GraphData) {
        return new Map((organisationModel?.nodes || []).map(n => [n._key, n.name] as [string, string]));
    }

    // into the view that explains it, looking at the same models
    private openLens(organisation: Organisation, scope: AnalysisScope, key: LensKey, focus?: string) {
        const lens = LENSES.find(l => l.key === key);
        if (!lens) return;
        this.scopeService.open(organisation._key, scope);
        void this.router.navigate([lens.url(organisation._key)], focus ? { queryParams: { focus } } : {});
    }
}
